// content/jobs.csv is the board: one row per open role, with the employer's columns
// repeated on each of its roles.

import { Job, Company, Salary, SalarySource, SalaryScope, placeKey } from './types';
import { parseCsv, splitList, splitNames, triState, int, anzscoCodes, unitGroupCodes } from './csv';
import { loadCompanies } from './companies';
import { dateValue } from './format';
import { setUnitGroupTitles, setInvitedScores } from './references';
import { dataUrl } from './dataUrl';

const JOBS_URL = dataUrl('/jobs.csv');

/** "80" -> 80, "" -> undefined. A blank cell means the occupation wasn't invited, which is
 * not the same thing as a score of zero. */
function invitedScore(value: string | undefined): number | undefined {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return undefined;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** The CSV's column names, in file order. */
export const COLUMNS = [
  'Company name',
  'State',
  'Segment',
  'Type',
  'Website',
  'Growth stage',
  'Employees',
  'Industries',
  'HQ city',
  'HQ address',
  'Company city',
  'Company state',
  'Tagline',
  'LinkedIn',
  'Job openings',
  'Accredited sponsor',
  'Hires international students',
  'Job title',
  'Job type',
  'ANZSCO occupation',
  'ANZSCO 2022',
  'ANZSCO 2013',
  'ANZSCO unit group',
  'ANZSCO unit group title',
  'OSCA occupation',
  'OSCA code',
  'Job city',
  'Job location city',
  'Job location state',
  'Job country',
  'Date posted',
  'Job URL',
  'Job ID',
  'Invited Score',
  // Enriched from levels.fyi by the data pipeline - see find-startups/enrich.py.
  'Employment type',
  'Job level',
  'Work arrangement',
  'Education level',
  'Base salary min',
  'Base salary max',
  'Base salary currency',
  'Base salary min AUD',
  'Base salary max AUD',
  'Company salary estimate AUD',
  'Salary source',
  'Salary source URL',
  'Advert posted',
  'Salary scope',
] as const;

function toCompany(row: Record<string, string>): Company {
  return {
    name: (row['Company name'] ?? '').trim(),
    state: (row['State'] ?? '').trim(),
    segment: (row['Segment'] ?? '').trim(),
    types: splitList(row['Type']),
    industries: splitList(row['Industries']),
    website: (row['Website'] ?? '').trim(),
    growthStage: (row['Growth stage'] ?? '').trim(),
    employees: (row['Employees'] ?? '').trim(),
    hqCity: (row['HQ city'] ?? '').trim(),
    hqAddress: (row['HQ address'] ?? '').trim(),
    location: placeKey(row['Company city'], row['Company state']),
    tagline: (row['Tagline'] ?? '').trim(),
    linkedin: (row['LinkedIn'] ?? '').trim(),
    openings: Number.parseInt(row['Job openings'] ?? '', 10) || 0,
    boardRoles: Number.parseInt(row['Board roles'] ?? '', 10) || 0,
    accreditedSponsor: triState(row['Accredited sponsor']),
    hiresInternationalStudents: triState(row['Hires international students']),
  };
}

/** Employers, folded out of the repeated columns. */
function foldCompanies(rows: Record<string, string>[]): Map<string, Company> {
  const byName = new Map<string, Company>();
  for (const row of rows) {
    const company = toCompany(row);
    if (!company.name) continue;
    const key = company.name.toLowerCase();
    const seen = byName.get(key);
    if (!seen) {
      byName.set(key, company);
      continue;
    }
    for (const [field, value] of Object.entries(company) as [keyof Company, never][]) {
      const current = seen[field];
      const empty =
        current === '' ||
        current === 0 ||
        current === undefined ||
        (Array.isArray(current) && current.length === 0);
      if (empty && value !== undefined) (seen[field] as unknown) = value;
    }
  }
  return byName;
}

/** The pay block, folded out of the CSV's salary columns. */
function toSalary(row: Record<string, string>): Salary {
  const min = int(row['Base salary min']);
  const max = int(row['Base salary max']);
  const minAud = int(row['Base salary min AUD']);
  const maxAud = int(row['Base salary max AUD']);
  const currency = (row['Base salary currency'] ?? '').trim().toUpperCase();
  const base =
    min || max || minAud || maxAud
      ? { min, max, currency: currency || 'AUD', minAud, maxAud }
      : undefined;
  const raw = (row['Salary source'] ?? '').trim().toLowerCase();
  const source: SalarySource =
    raw === 'advert' || raw === 'glassdoor' || raw === 'levels.fyi' ? raw : '';
  const rawScope = (row['Salary scope'] ?? '').trim().toLowerCase();
  const scope: SalaryScope = rawScope === 'role' || rawScope === 'company' ? rawScope : '';
  return {
    base,
    estimateAud: int(row['Company salary estimate AUD']),
    source,
    sourceUrl: (row['Salary source URL'] ?? '').trim(),
    isEstimate: source === 'glassdoor' || source === 'levels.fyi',
    scope,
  };
}

function toJob(row: Record<string, string>, company: Company): Job {
  return {
    id: (row['Job ID'] ?? '').trim(),
    title: (row['Job title'] ?? '').trim(),
    type: (row['Job type'] ?? '').trim(),
    occupationNames: splitNames(row['ANZSCO occupation']),
    anzsco2022: anzscoCodes(row['ANZSCO 2022']),
    anzsco2013: anzscoCodes(row['ANZSCO 2013']),
    anzscoUnitGroups: unitGroupCodes(row['ANZSCO unit group']),
    anzscoUnitGroupTitles: splitNames(row['ANZSCO unit group title']),
    oscaCodes: anzscoCodes(row['OSCA code']),
    oscaNames: splitNames(row['OSCA occupation']),
    invitedScore: invitedScore(row['Invited Score']),
    city: (row['Job city'] ?? '').trim(),
    location: placeKey(row['Job location city'], row['Job location state']),
    // Only alongside a city, as `location` is: a state on its own is the employer's guess.
    locationState: (row['Job location city'] ?? '').trim()
      ? (row['Job location state'] ?? '').trim()
      : '',
    state: (row['State'] ?? '').trim(),
    country: (row['Job country'] ?? '').trim(),
    posted: (row['Advert posted'] || row['Date posted'] || '').trim(),
    postedApprox: Boolean((row['Advert posted'] ?? '').trim()),
    applyUrl: (row['Job URL'] ?? '').trim(),
    company,
    employmentType: (row['Employment type'] ?? '').trim(),
    jobLevels: splitNames(row['Job level']),
    workArrangements: splitNames(row['Work arrangement']),
    educationLevels: splitNames(row['Education level']),
    salary: toSalary(row),
  };
}

/** Set once the whole board has registered its reference maps, so a snapshot can't overwrite them. */
let boardRegistered = false;

/**
 * Rows -> roles, newest first.
 *
 * `snapshot` marks rows that are only a slice of the board (the pre-loaded newest days). It
 * registers the unit-group titles and invited scores those rows carry - the reference maps
 * are global, and a slice has to be readable on its own - but only until the full board has
 * done the same. The snapshot is a few hundred rows against thousands, so it is very likely
 * to land first, but nothing guarantees it, and a slice arriving last must not replace the
 * maps the whole board built.
 */
export function toJobs(rows: Record<string, string>[], snapshot = false): Job[] {
  const companies = foldCompanies(rows);

  // The four-digit titles live only in this file, so they are registered for
  // whatever needs to name a unit group later.
  const titles: Record<string, string> = {};
  for (const row of rows) {
    const codes = unitGroupCodes(row['ANZSCO unit group']);
    const names = splitNames(row['ANZSCO unit group title']);
    codes.forEach((code, i) => {
      if (names[i] && !titles[code]) titles[code] = names[i];
    });
  }
  if (!snapshot || !boardRegistered) setUnitGroupTitles(titles);

  // Likewise the invited scores: SkillSelect scores an occupation, not a row, so every code
  // a row carries gets the same score. The first row to name a code wins, same as titles
  // above - every row for one occupation carries the same figure anyway.
  const scores: Record<string, number> = {};
  for (const row of rows) {
    const score = invitedScore(row['Invited Score']);
    if (score === undefined) continue;
    for (const code of [...anzscoCodes(row['ANZSCO 2022']), ...anzscoCodes(row['ANZSCO 2013'])]) {
      if (!(code in scores)) scores[code] = score;
    }
  }
  if (!snapshot || !boardRegistered) setInvitedScores(scores);
  if (!snapshot) boardRegistered = true;

  return rows
    .map((row) => {
      const name = (row['Company name'] ?? '').trim().toLowerCase();
      return toJob(row, companies.get(name) ?? toCompany(row));
    })
    .filter((job) => job.id && job.title)
    .sort((a, b) => {
      const byDate = (dateValue(b.posted) || 0) - (dateValue(a.posted) || 0);
      if (byDate) return byDate;
      // Same day - and hundreds of roles can share Dealroom's batch date. A
      // LinkedIn job id increases with time, so it orders them within the day;
      // the Job id is the last resort so the sort stays stable.
      return linkedinId(b.applyUrl) - linkedinId(a.applyUrl) || b.id.localeCompare(a.id);
    });
}

/** The numeric id in a LinkedIn job URL, or 0. LinkedIn allocates these roughly
 * in time order, so a larger one is newer. */
export function linkedinId(url: string): number {
  const match = url.match(/linkedin\.com\/jobs\/view\/(?:[a-z0-9-]*-)?(\d{6,})/i);
  return match ? Number(match[1]) : 0;
}

/** How long a role stays on the board after it was posted. */
export const MONTHS_LISTED = 2;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The same ISO date, that many calendar months earlier. */
function monthsBefore(iso: string, months: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() - months);
  const lastOfMonth = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)
  ).getUTCDate();
  date.setUTCDate(Math.min(day, lastOfMonth));
  return date.toISOString().slice(0, 10);
}

/**
 * Whether a role is recent enough to still be worth showing, both dates being YYYY-MM-DD -
 * ISO dates compare correctly as plain strings, which sidesteps the timezone trap in
 * parsing them.
 */
export function isRecent(job: Job, today: string): boolean {
  const posted = job.posted.trim();
  if (!ISO_DATE.test(posted)) return true;
  const cutoff = monthsBefore(today, MONTHS_LISTED);
  return !cutoff || posted >= cutoff;
}

/** The employers on the board, alphabetical, each with the roles it has listed. */
export function companiesFrom(jobs: Job[]): { company: Company; jobs: Job[] }[] {
  const byName = new Map<string, { company: Company; jobs: Job[] }>();
  for (const job of jobs) {
    const key = job.company.name.toLowerCase();
    if (!key) continue;
    const seen = byName.get(key);
    if (seen) seen.jobs.push(job);
    else byName.set(key, { company: job.company, jobs: [job] });
  }
  return Array.from(byName.values()).sort((a, b) => a.company.name.localeCompare(b.company.name));
}

/** Fills each employer's gaps from the companies file. */
function enrich(jobs: Job[], companies: Company[]): Job[] {
  const byName = new Map(companies.map((c) => [c.name.trim().toLowerCase(), c]));
  const merged = new Map<string, Company>();

  return jobs.map((job) => {
    const key = job.company.name.trim().toLowerCase();
    const known = byName.get(key);
    if (!known) return job;

    // Merged once per employer, not once per role: 2,000 roles across 370 companies would
    // otherwise rebuild the same object six times over.
    let company = merged.get(key);
    if (!company) {
      company = { ...job.company };
      for (const field of Object.keys(company) as (keyof Company)[]) {
        const mine = company[field];
        const empty =
          mine === '' || mine === undefined || (Array.isArray(mine) && mine.length === 0);
        if (empty) (company[field] as unknown) = known[field];
      }
      merged.set(key, company);
    }
    return { ...job, company };
  });
}

const RECENT_URL = dataUrl('/recent-jobs.csv');

let recent: Promise<Job[]> | null = null;

/**
 * The newest few days of roles, written next to the board at build time (see
 * scripts/seo-assets.js) so there is something to show while the whole board - several
 * megabytes of CSV - downloads and parses.
 *
 * Never rejects: it is a head start, not something the board depends on. The file is absent
 * in development and on a build from before it existed, and either is just "no snapshot".
 * Memoised, so index.tsx can start the download before React mounts and the hook that reads
 * it later gets the same request rather than making another.
 */
export function loadRecentJobs(): Promise<Job[]> {
  if (!recent) {
    recent = (async () => {
      try {
        const res = await fetch(RECENT_URL);
        if (!res.ok) return [];
        return toJobs(parseCsv(await res.text()), true);
      } catch {
        return [];
      }
    })();
  }
  return recent;
}

export async function loadJobs(): Promise<Job[]> {
  // Both are fetched together rather than in sequence: neither depends on the other's
  // contents, and the companies file is the larger of the two.
  const [res, companies] = await Promise.all([
    fetch(JOBS_URL),
    // A missing or broken companies file costs the two merged columns, not the board.
    loadCompanies().catch(() => [] as Company[]),
  ]);
  if (!res.ok) throw new Error(`Could not load jobs (${res.status})`);
  return enrich(toJobs(parseCsv(await res.text())), companies);
}
